import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CategoryView, ProductLine } from '@popcart/core/v1';
import { api, errorMessage, errorStatus } from '../../api';
import { ConfirmDialog, EmptyState, ErrorState, LineDot, Loading, useTitle } from '../../ui/bits';
import { Fig, PageHead, Ring, RowMenu, Stat } from '../../ui/viz';
import { Meter, StDot, menuItems } from '../../catalog/kit';
import { count } from '../../lib/format.js';
import { STATE_ART } from '../../lib/voice.js';
import { useToast } from '../../ui/toast';
import { catalogKeys } from '../../catalog/keys';
import { LINE_LABEL, LINES } from '../../catalog/lib/list.js';
import {
  buildCategoryInput,
  buildCategoryPatch,
  canDeleteCategory,
  categoryDraft,
  emptyCategory,
  slugify,
  sortCategories,
  validateCategory,
  type CategoryDraft,
} from '../../catalog/lib/categories.js';
import '../../catalog/catalog.css';
import { Select } from '../../ui/controls';

/** /ops/categories — the categories shops pick from and customers filter by. */
export function CategoriesPage() {
  useTitle('Categories');
  const qc = useQueryClient();
  const toast = useToast();
  const cats = useQuery({ queryKey: catalogKeys.opsCategories, queryFn: () => api.opsCategories() });
  const all = cats.data ?? [];
  const sorted = sortCategories(all);
  const [editing, setEditing] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<CategoryView | null>(null);
  const [blocked, setBlocked] = useState<{ cat: CategoryView; message: string } | null>(null);

  const invalidate = () => void qc.invalidateQueries({ queryKey: catalogKeys.all });

  const patch = useMutation({
    mutationFn: (v: { cat: CategoryView; patch: Parameters<typeof api.updateCategory>[1] }) => api.updateCategory(v.cat.id, v.patch),
    onSuccess: (c, v) => {
      qc.setQueryData<CategoryView[]>(catalogKeys.opsCategories, (xs) => xs?.map((x) => (x.id === c.id ? { ...x, ...c, productCount: c.productCount ?? x.productCount } : x)));
      toast('isActive' in v.patch && Object.keys(v.patch).length === 1 ? (c.isActive ? `${c.name} is back in pickers and filters.` : `${c.name} is deactivated. Its products keep it.`) : `${c.name} is saved.`);
      setBlocked(null);
    },
    onError: (err) => toast(errorMessage(err, "Couldn't save the category. Try again."), 'error'),
    onSettled: invalidate,
  });

  const del = useMutation({
    mutationFn: (c: CategoryView) => api.deleteCategory(c.id),
    onSuccess: (_r, c) => {
      toast(`${c.name} is deleted.`);
      setToDelete(null);
    },
    onError: (err, c) => {
      setToDelete(null);
      if (errorStatus(err) === 409) {
        const server = errorMessage(err, '');
        setBlocked({ cat: c, message: `${server ? `${server} ` : 'Products use this category, so it can’t be deleted. '}Deactivate it instead: it leaves pickers and customer filters, and those products keep it.` });
      }
      else toast(errorMessage(err, "Couldn't delete the category. Try again."), 'error');
    },
    onSettled: invalidate,
  });

  const askDelete = (c: CategoryView) => {
    const chk = canDeleteCategory(c);
    if (chk.ok) setToDelete(c);
    else setBlocked({ cat: c, message: chk.message });
  };

  const active = all.filter((c) => c.isActive).length;
  const used = all.reduce((n, c) => n + (c.productCount ?? 0), 0);
  const top = Math.max(1, ...all.map((c) => c.productCount ?? 0));
  const byLine = LINES.map((l) => ({ line: l, n: all.filter((c) => c.line === l).length }));
  const anyLine = all.filter((c) => !c.line).length;

  const rowMenu = (c: CategoryView) =>
    menuItems([
      {
        label: c.isActive ? 'Deactivate' : 'Activate',
        icon: c.isActive ? 'bellOff' : 'check',
        disabled: patch.isPending && patch.variables?.cat.id === c.id,
        onSelect: () => patch.mutate({ cat: c, patch: { isActive: !c.isActive } }),
      },
      { label: 'Delete', icon: 'close', tone: 'danger', onSelect: () => askDelete(c) },
    ]);

  return (
    <>
      <PageHead title="Categories" meta={cats.data ? `${active} active · ${all.length - active} inactive` : undefined} />

      {cats.data ? (
        <div className="dv-stats dv-rise" style={{ ['--i' as string]: 0 }}>
          <Stat label="Active" value={`${active} / ${all.length}`} visual={<Ring pct={all.length ? (active / all.length) * 100 : 0} tone="good" />} />
          <Stat label="Products tagged" value={count(used)} sub={`across ${all.length} categories`} />
          <section className="card dv-stat" aria-label="By line">
            <span className="dv-label">By line</span>
            <div className="mk-linebars">
              {byLine.map((b) => (
                <span key={b.line} title={`${LINE_LABEL[b.line]}: ${b.n}`}>
                  <LineDot line={b.line} />
                  <span className="nm">{LINE_LABEL[b.line]}</span>
                  <Fig text={String(b.n)} size="sm" animate={false} />
                </span>
              ))}
              {anyLine ? (
                <span title={`Any line: ${anyLine}`}>
                  <i className="mk-dot lg off" aria-hidden="true" />
                  <span className="nm">Any</span>
                  <Fig text={String(anyLine)} size="sm" animate={false} />
                </span>
              ) : null}
            </div>
          </section>
        </div>
      ) : null}

      <AddCategory existing={all} onAdded={invalidate} />

      <section className="card dv-card dv-rise" aria-labelledby="cat-list-h" style={{ ['--i' as string]: 2 }}>
        <div className="dv-card-h">
          <h2 id="cat-list-h" title="Shops pick these for their products; customers filter by them. Inactive ones leave pickers and filters, and products keep them.">
            All categories
          </h2>
          {cats.data ? <span className="dv-chip">{all.length}</span> : null}
        </div>
        {cats.isError ? (
          <ErrorState error={cats.error} onRetry={() => void cats.refetch()} />
        ) : !cats.data ? (
          <Loading label="Loading categories" rows={3} />
        ) : sorted.length === 0 ? (
          <EmptyState art={STATE_ART.noProducts} title="No categories yet">
            Add the first one above, like Seafood for Fresh or Inihaw for Grill.
          </EmptyState>
        ) : (
          <div className="table-wrap mk-wrap">
            <table className="mk-table">
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Line</th>
                  <th scope="col" className="num">
                    Sort
                  </th>
                  <th scope="col" className="num">
                    Products
                  </th>
                  <th scope="col">Status</th>
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((c) =>
                  editing === c.id ? (
                    <EditRow key={c.id} cat={c} all={all} busy={patch.isPending} onCancel={() => setEditing(null)} onSave={(p) => patch.mutate({ cat: c, patch: p }, { onSuccess: () => setEditing(null) })} />
                  ) : (
                    <tr key={c.id} className={c.isActive ? undefined : 'cat-inactive'}>
                      <td>
                        <span className="stack s4">
                          <b className="mk-name">{c.name}</b>
                          <span className="mk-sub t-mono">{c.slug}</span>
                        </span>
                      </td>
                      <td>
                        {c.line ? (
                          <span className="mk-st">
                            <LineDot line={c.line} />
                            {LINE_LABEL[c.line]}
                          </span>
                        ) : (
                          <span className="mk-st">
                            <i className="mk-dot lg off" aria-hidden="true" />
                            Any
                          </span>
                        )}
                      </td>
                      <td className="num">
                        <span className="mk-numtxt muted">{c.sort}</span>
                      </td>
                      <td className="num">
                        <span className="mk-figbar">
                          <Fig text={c.productCount === null || c.productCount === undefined ? '—' : String(c.productCount)} size="xs" animate={false} />
                          <Meter value={(c.productCount ?? 0) / top} />
                        </span>
                      </td>
                      <td>
                        <StDot tone={c.isActive ? 'ok' : 'off'}>{c.isActive ? 'Active' : 'Inactive'}</StDot>
                      </td>
                      <td className="num">
                        <div className="actions">
                          <button type="button" className="btn btn-quiet" aria-label={`Edit ${c.name}`} onClick={() => setEditing(c.id)}>
                            Edit
                          </button>
                          <RowMenu label={`More for ${c.name}`} items={rowMenu(c)} />
                        </div>
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <ConfirmDialog
        open={toDelete !== null}
        title={`Delete ${toDelete?.name ?? 'this category'}?`}
        body="No product uses it, so it can be deleted. Customers stop seeing it as a filter."
        confirmLabel="Delete category"
        busy={del.isPending}
        onConfirm={() => toDelete && del.mutate(toDelete)}
        onClose={() => setToDelete(null)}
      />
      <BlockedDialog
        blocked={blocked}
        busy={patch.isPending}
        onDeactivate={(c) => patch.mutate({ cat: c, patch: { isActive: false } })}
        onClose={() => setBlocked(null)}
      />
    </>
  );
}

function AddCategory({ existing, onAdded }: { existing: CategoryView[]; onAdded(): void }) {
  const toast = useToast();
  const [d, setD] = useState<CategoryDraft>(emptyCategory);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const create = useMutation({
    mutationFn: () => api.createCategory(buildCategoryInput(d)),
    onSuccess: (c) => {
      toast(`${c.name} is added. Shops can pick it now.`);
      setD(emptyCategory());
      setErrors({});
      onAdded();
    },
    onError: (err) => {
      const field = (err as { field?: string }).field;
      const f = field === 'name' || field === 'slug' ? 'name' : field === 'line' || field === 'sort' ? field : 'form';
      setErrors({ [f]: errorMessage(err, "Couldn't add the category. Try again.") });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const v = validateCategory(d, existing, null);
    setErrors(v);
    if (Object.keys(v).length) {
      document.getElementById(v.name ? 'nc-name' : v.line ? 'nc-line' : 'nc-sort')?.focus();
      return;
    }
    create.mutate();
  };
  const set = <K extends keyof CategoryDraft>(k: K, v: CategoryDraft[K]) => {
    setD((x) => ({ ...x, [k]: v }));
    setErrors((e) => (e[k as string] || e.form ? {} : e));
  };
  const slug = slugify(d.name);
  return (
    <section className="card dv-card dv-rise" aria-labelledby="cat-add-h" style={{ ['--i' as string]: 1 }}>
      <div className="dv-card-h">
        <h2 id="cat-add-h">Add a category</h2>
      </div>
      <form className="cat-catform" onSubmit={submit} noValidate>
        <div className="field">
          <label htmlFor="nc-name">Name</label>
          <input id="nc-name" className="input" maxLength={40} value={d.name} aria-invalid={errors.name ? true : undefined} aria-describedby={errors.name ? 'nc-name-err' : 'nc-name-help'} onChange={(e) => set('name', e.target.value)} />
          {errors.name ? (
            <span id="nc-name-err" className="pill st-error err-pill cat-wrap">
              {errors.name}
            </span>
          ) : (
            <span id="nc-name-help" className="help">
              {slug ? `Link: ${slug}` : 'Like Seafood or Inihaw'}
            </span>
          )}
        </div>
        <div className="field">
          <label htmlFor="nc-line">Line</label>
          <Select id="nc-line" className="input" value={d.line} aria-invalid={errors.line ? true : undefined} aria-describedby={errors.line ? 'nc-line-err' : undefined} onChange={(e) => set('line', e.target.value as ProductLine | '')}>
            <option value="">Choose</option>
            {LINES.map((l) => (
              <option key={l} value={l}>
                {LINE_LABEL[l]}
              </option>
            ))}
          </Select>
          {errors.line ? (
            <span id="nc-line-err" className="pill st-error err-pill">
              {errors.line}
            </span>
          ) : null}
        </div>
        <div className="field">
          <label htmlFor="nc-sort">Sort</label>
          <input id="nc-sort" className="input" inputMode="numeric" value={d.sort} aria-invalid={errors.sort ? true : undefined} aria-describedby={errors.sort ? 'nc-sort-err' : undefined} onChange={(e) => set('sort', e.target.value)} />
          {errors.sort ? (
            <span id="nc-sort-err" className="pill st-error err-pill cat-wrap">
              {errors.sort}
            </span>
          ) : null}
        </div>
        <label className="check-label">
          <input type="checkbox" className="check" checked={d.isActive} onChange={(e) => set('isActive', e.target.checked)} />
          <span className="t-label">Active</span>
        </label>
        <button type="submit" className="btn btn-primary" disabled={create.isPending} aria-busy={create.isPending || undefined}>
          {create.isPending ? 'Adding…' : 'Add category'}
        </button>
      </form>
      {errors.form ? (
        <span className="pill st-error err-pill cat-wrap" role="alert">
          {errors.form}
        </span>
      ) : null}
    </section>
  );
}

function EditRow({ cat, all, busy, onCancel, onSave }: { cat: CategoryView; all: CategoryView[]; busy: boolean; onCancel(): void; onSave(p: ReturnType<typeof buildCategoryPatch>): void }) {
  const [d, setD] = useState<CategoryDraft>(() => categoryDraft(cat));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const id = `ec-${cat.id}`;
  const save = () => {
    const v = validateCategory(d, all, cat.id);
    setErrors(v);
    if (Object.keys(v).length) return;
    const p = buildCategoryPatch(d, cat);
    if (!Object.keys(p).length) onCancel();
    else onSave(p);
  };
  const err = errors.name || errors.line || errors.sort;
  return (
    <tr>
      <td>
        <label className="sr-only" htmlFor={`${id}-name`}>
          Name
        </label>
        <input
          id={`${id}-name`}
          className="input"
          maxLength={40}
          value={d.name}
          autoFocus
          aria-invalid={errors.name ? true : undefined}
          aria-describedby={err ? `${id}-err` : undefined}
          onChange={(e) => setD({ ...d, name: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') onCancel();
          }}
        />
        <span className="mk-sub t-mono">{slugify(d.name) || '—'}</span>
      </td>
      <td>
        <label className="sr-only" htmlFor={`${id}-line`}>
          Line
        </label>
        <Select id={`${id}-line`} className="input" value={d.line} aria-invalid={errors.line ? true : undefined} onChange={(e) => setD({ ...d, line: e.target.value as ProductLine | '' })}>
          <option value="">Choose</option>
          {LINES.map((l) => (
            <option key={l} value={l}>
              {LINE_LABEL[l]}
            </option>
          ))}
        </Select>
      </td>
      <td className="num">
        <label className="sr-only" htmlFor={`${id}-sort`}>
          Sort
        </label>
        <input id={`${id}-sort`} className="input cat-num" inputMode="numeric" value={d.sort} aria-invalid={errors.sort ? true : undefined} onChange={(e) => setD({ ...d, sort: e.target.value })} />
      </td>
      <td className="num">{cat.productCount ?? '—'}</td>
      <td>
        <label className="check-label">
          <input type="checkbox" className="check" checked={d.isActive} onChange={(e) => setD({ ...d, isActive: e.target.checked })} />
          <span className="t-caption">Active</span>
        </label>
      </td>
      <td>
        <div className="actions">
          {err ? (
            <span id={`${id}-err`} className="pill st-error err-pill cat-wrap" role="alert">
              {err}
            </span>
          ) : null}
          <button type="button" className="btn" disabled={busy} onClick={save}>
            {busy ? 'Saving…' : 'Save'}
          </button>
          <button type="button" className="btn btn-quiet" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </td>
    </tr>
  );
}

/** Delete refused while products use the category: explain, offer to deactivate. */
function BlockedDialog({ blocked, busy, onDeactivate, onClose }: { blocked: { cat: CategoryView; message: string } | null; busy: boolean; onDeactivate(c: CategoryView): void; onClose(): void }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (blocked && !d.open) d.showModal();
    else if (!blocked && d.open) d.close();
  }, [blocked]);
  return (
    <dialog ref={ref} className="dialog" aria-labelledby="blocked-title" onClose={onClose}>
      <div className="stack s24">
        <h2 id="blocked-title" className="t-headline">
          {blocked ? `${blocked.cat.name} is in use` : 'In use'}
        </h2>
        <p className="t-body">{blocked?.message}</p>
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn" onClick={onClose}>
            Keep it
          </button>
          {blocked?.cat.isActive ? (
            <button type="button" className="btn" disabled={busy} aria-busy={busy || undefined} onClick={() => blocked && onDeactivate(blocked.cat)}>
              {busy ? 'Working…' : 'Deactivate instead'}
            </button>
          ) : null}
        </div>
      </div>
    </dialog>
  );
}
